import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
	Divider,
	Checkbox,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Stack,
} from "@mui/material";
import { useState } from "react";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";
import { format } from "date-fns";

interface IComission {
	id: string;
	dueDate: string | null;
	value: string;
	type: string;
	description: string;
	createdAt?: string | null;
}

interface ModalSelectComissionsToCancelProps {
	installments: IComission[]; // Tipo IComission
	onConfirm: (selectedIds: string[]) => void;
	onClose: () => void;
}

export const ModalSelectComissionsToCancel = ({ installments, onConfirm, onClose }: ModalSelectComissionsToCancelProps) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const sortedComissions = [...installments].sort((a, b) => {
		const dateA = new Date(a.dueDate || a.createdAt || 0).getTime();
		const dateB = new Date(b.dueDate || b.createdAt || 0).getTime();
		return dateA - dateB;
	});

	const allSelected = selectedIds.length === sortedComissions.length;
	const partiallySelected = selectedIds.length > 0 && !allSelected;

	const toggleSelection = (id: string) => {
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const toggleSelectAll = () => {
		if (allSelected) {
			setSelectedIds([]);
		} else {
			setSelectedIds(sortedComissions.map((item) => item.id));
		}
	};

	const handleConfirm = () => {
		if (selectedIds.length > 0) {
			onConfirm(selectedIds);
		}
	};


	const formatProventoType = (type: string) => {
		const lowerType = type.toLowerCase();
		if (lowerType === "prize") return "Premiação";
		if (lowerType === "bonus") return "Bônus";
		if (lowerType === "discount") return "Desconto";
		return type;
	};

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					Selecione registros para cancelar
				</Typography>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 2, maxHeight: "60vh" }}>
				{sortedComissions.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nenhuma comissão pendente encontrada para esta ação.
					</Typography>
				) : (
					<List dense disablePadding>
						{/* Select All */}
						<ListItem divider>
							<ListItemIcon>
								<Checkbox edge="start" checked={allSelected} indeterminate={partiallySelected} onChange={toggleSelectAll} />
							</ListItemIcon>
							<ListItemText primary="Selecionar registros para cancelar" primaryTypographyProps={{ fontWeight: 500 }} />
						</ListItem>

						{/* Lista ordenada */}
						{sortedComissions.map((item) => {
							const isDiscount = item.type.toLowerCase() === "discount";
							const dueDate = item.dueDate || item.createdAt;

							return (
								<ListItem
									key={item.id}
									divider
									secondaryAction={
										<Typography
											variant="body2"
											fontWeight={500}
											color={isDiscount || parseFloat(item.value) < 0 ? "error.main" : undefined}
										>
											{isDiscount || parseFloat(item.value) < 0
												? `- ${globalFormatCurrencyBRL(item.value)}`
												: globalFormatCurrencyBRL(item.value)}
										</Typography>
									}
								>
									<ListItemIcon>
										<Checkbox edge="start" checked={selectedIds.includes(item.id)} onChange={() => toggleSelection(item.id)} />
									</ListItemIcon>
									<ListItemText
										primary={formatProventoType(item.type)}
										secondary={
											<Stack direction="column" spacing={0}>
												<Typography variant="caption" color="text.secondary">
													{item.description}
												</Typography>
												<Typography variant="caption">
													Vencimento: {dueDate ? format(new Date(dueDate), "dd/MM/yyyy") : "N/A"}
												</Typography>
											</Stack>
										}
									/>
								</ListItem>
							);
						})}
					</List>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Stack direction="row" justifyContent="space-between" width="100%">
					<Button variant="outlined" onClick={onClose}>
						Fechar
					</Button>
					<Button variant="contained" color="primary" disabled={selectedIds.length === 0} onClick={handleConfirm}>
						Continuar ({selectedIds.length})
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};
